import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import integrationsService from '../services/integrationsService';
import rolesService from '../services/rolesService';
import { Plus, Trash, ListBullets, Copy, PencilSimple, X } from '@phosphor-icons/react';

const emptyKeyForm = { name: '', scopes: [] };
const emptyWebhookForm = { url: '', subscribed_events: [] };
const emptyDeviceForm = { device_serial: '', name: '', location: '', purpose: '' };
const DEVICE_PURPOSE_LABELS = { canteen: 'Canteen', staff_attendance: 'Staff Attendance', student_attendance: 'Student Attendance' };

function IntegrationsPage() {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]); // public_api-module permission codes, reused as API key scopes
  const [supportedEvents, setSupportedEvents] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);

  const [showKeyModal, setShowKeyModal] = useState(false);
  const [keyForm, setKeyForm] = useState(emptyKeyForm);
  const [submittingKey, setSubmittingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null); // { name, raw_key } shown once
  const [revokeTarget, setRevokeTarget] = useState(null);

  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [webhookForm, setWebhookForm] = useState(emptyWebhookForm);
  const [submittingWebhook, setSubmittingWebhook] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null); // { url, raw_secret } shown once
  const [deleteWebhookTarget, setDeleteWebhookTarget] = useState(null);
  const [deliveriesTarget, setDeliveriesTarget] = useState(null);
  const [deliveries, setDeliveries] = useState([]);

  const [devices, setDevices] = useState([]);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [deviceForm, setDeviceForm] = useState(emptyDeviceForm);
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [submittingDevice, setSubmittingDevice] = useState(false);
  const [deleteDeviceTarget, setDeleteDeviceTarget] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [catalogData, eventsData, keysData, webhooksData, devicesData] = await Promise.all([
        rolesService.listPermissionCatalog(),
        integrationsService.listSupportedEvents(),
        integrationsService.listApiKeys(),
        integrationsService.listWebhooks(),
        integrationsService.listBiometricDevices(),
      ]);
      // Only public_api-module codes actually gate anything in routers/public_api.py --
      // the full catalog also has staff-facing codes (finance.*, hr.*, academics.*, ...)
      // meant for role composition (see RolesPermissionsPage.js), which would just be
      // inert if picked as an API key scope.
      const fullCatalog = Array.isArray(catalogData) ? catalogData : [];
      setCatalog(fullCatalog.filter((perm) => perm.module === 'public_api'));
      setSupportedEvents(Array.isArray(eventsData) ? eventsData : []);
      setApiKeys(Array.isArray(keysData) ? keysData : []);
      setWebhooks(Array.isArray(webhooksData) ? webhooksData : []);
      setDevices(Array.isArray(devicesData) ? devicesData : []);
    } catch (error) {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  // ---- API Keys ----

  const toggleScope = (code) => {
    setKeyForm((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(code) ? prev.scopes.filter((c) => c !== code) : [...prev.scopes, code],
    }));
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!keyForm.name.trim()) {
      toast.error('Key name is required');
      return;
    }
    setSubmittingKey(true);
    try {
      const created = await integrationsService.createApiKey(keyForm);
      setShowKeyModal(false);
      setKeyForm(emptyKeyForm);
      setRevealedKey({ name: created.name, raw_key: created.raw_key });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create API key');
    } finally {
      setSubmittingKey(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await integrationsService.revokeApiKey(revokeTarget);
      toast.success('API key revoked');
      setRevokeTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to revoke key');
    }
  };

  // ---- Webhooks ----

  const toggleEvent = (eventType) => {
    setWebhookForm((prev) => ({
      ...prev,
      subscribed_events: prev.subscribed_events.includes(eventType)
        ? prev.subscribed_events.filter((e) => e !== eventType)
        : [...prev.subscribed_events, eventType],
    }));
  };

  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    if (!webhookForm.url.trim()) {
      toast.error('Endpoint URL is required');
      return;
    }
    setSubmittingWebhook(true);
    try {
      const created = await integrationsService.createWebhook(webhookForm);
      setShowWebhookModal(false);
      setWebhookForm(emptyWebhookForm);
      setRevealedSecret({ url: created.url, raw_secret: created.raw_secret });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create webhook endpoint');
    } finally {
      setSubmittingWebhook(false);
    }
  };

  const confirmDeleteWebhook = async () => {
    if (!deleteWebhookTarget) return;
    try {
      await integrationsService.deleteWebhook(deleteWebhookTarget);
      toast.success('Webhook endpoint deleted');
      setDeleteWebhookTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete webhook endpoint');
    }
  };

  const openDeliveries = async (endpoint) => {
    setDeliveriesTarget(endpoint);
    try {
      const data = await integrationsService.listWebhookDeliveries(endpoint.id);
      setDeliveries(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load delivery log');
    }
  };

  // ---- Biometric Devices ----

  const openAddDevice = () => {
    setEditingDeviceId(null);
    setDeviceForm(emptyDeviceForm);
    setShowDeviceModal(true);
  };

  const openEditDevice = (device) => {
    setEditingDeviceId(device.id);
    setDeviceForm({ device_serial: device.device_serial, name: device.name, location: device.location || '', purpose: device.purpose });
    setShowDeviceModal(true);
  };

  const handleSubmitDevice = async (e) => {
    e.preventDefault();
    if (!deviceForm.name.trim() || !deviceForm.device_serial.trim()) {
      toast.error('Device serial and name are required');
      return;
    }
    if (!deviceForm.purpose) {
      toast.error('Choose what this device is for — Canteen or Staff Attendance');
      return;
    }
    setSubmittingDevice(true);
    try {
      if (editingDeviceId) {
        await integrationsService.updateBiometricDevice(editingDeviceId, { name: deviceForm.name, location: deviceForm.location, purpose: deviceForm.purpose });
        toast.success('Device updated');
      } else {
        await integrationsService.createBiometricDevice(deviceForm);
        toast.success('Device registered');
      }
      setShowDeviceModal(false);
      setDeviceForm(emptyDeviceForm);
      setEditingDeviceId(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save device');
    } finally {
      setSubmittingDevice(false);
    }
  };

  const toggleDeviceActive = async (device) => {
    try {
      await integrationsService.updateBiometricDevice(device.id, { is_active: !device.is_active });
      toast.success(device.is_active ? 'Device deactivated' : 'Device activated');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update device');
    }
  };

  const confirmDeleteDevice = async () => {
    if (!deleteDeviceTarget) return;
    try {
      await integrationsService.deleteBiometricDevice(deleteDeviceTarget);
      toast.success('Device removed');
      setDeleteDeviceTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to remove device');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Could not copy — select and copy manually');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading integrations...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>API & Webhooks</h1>
            <p>API keys and webhooks for third-party systems to read from and be notified by Campusio.</p>
          </div>
        </div>

        <h2 style={{ marginTop: 24 }}>API Keys</h2>
        <p style={{ color: 'var(--text-secondary, #666)' }}>
          Third-party systems authenticate to <code>/api/public/v1/*</code> with an <code>X-API-Key</code> header instead of a user login.
        </p>
        <button className="btn btn-primary" onClick={() => setShowKeyModal(true)} style={{ marginBottom: 12 }}>
          <Plus size={20} />
          New API Key
        </button>
        <div className="table-container">
          {apiKeys.length === 0 ? (
            <div className="empty-state"><p>No API keys yet.</p></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Name</th><th>Prefix</th><th>Scopes</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {apiKeys.map((key) => (
                  <tr key={key.id}>
                    <td>{key.name}</td>
                    <td><code>{key.key_prefix}…</code></td>
                    <td>{key.scopes.length ? key.scopes.join(', ') : '—'}</td>
                    <td>{key.is_active ? 'Active' : 'Revoked'}</td>
                    <td className="action-buttons">
                      {key.is_active && (
                        <button className="btn-icon btn-danger" title="Revoke" onClick={() => setRevokeTarget(key.id)}>
                          <Trash size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <h2 style={{ marginTop: 32 }}>Webhooks</h2>
        <p style={{ color: 'var(--text-secondary, #666)' }}>
          Subscribe an endpoint to Campusio events. Deliveries are signed with HMAC-SHA512 in the <code>X-Campusio-Signature</code> header.
        </p>
        <button className="btn btn-primary" onClick={() => setShowWebhookModal(true)} style={{ marginBottom: 12 }}>
          <Plus size={20} />
          New Webhook
        </button>
        <div className="table-container">
          {webhooks.length === 0 ? (
            <div className="empty-state"><p>No webhook endpoints yet.</p></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>URL</th><th>Events</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {webhooks.map((endpoint) => (
                  <tr key={endpoint.id}>
                    <td>{endpoint.url}</td>
                    <td>{endpoint.subscribed_events.join(', ') || '—'}</td>
                    <td>{endpoint.is_active ? 'Active' : 'Disabled'}</td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Delivery log" onClick={() => openDeliveries(endpoint)}>
                        <ListBullets size={18} />
                      </button>
                      <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteWebhookTarget(endpoint.id)}>
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <h2 style={{ marginTop: 32 }}>Biometric Devices</h2>
        <p style={{ color: 'var(--text-secondary, #666)' }}>
          Fingerprint/face-scan devices registered to push canteen scans, staff attendance punches, or student gate
          check-in/out. Two ways to connect one, depending on what the device supports:
        </p>
        <ul style={{ color: 'var(--text-secondary, #666)', marginTop: -8 }}>
          <li>
            <strong>Vendor middleware that can call a custom API</strong> — point it at{' '}
            <code>POST /api/public/v1/biometric/student-punch</code> or <code>/staff-punch</code> with an{' '}
            <code>X-API-Key</code> header from an API key scoped to <code>attendance.record.manage</code> (students) or{' '}
            <code>staff_attendance.record.manage</code> (staff), and <code>device_serial</code> matching what's registered here.
          </li>
          <li>
            <strong>A raw device with no middleware</strong> (speaks the ADMS/push protocol directly, e.g. most
            ZKTeco terminals) — point it at <code>POST /api/adms/iclock/cdata</code>, no API key needed; the device's
            own serial number and the <strong>Purpose</strong> chosen below decide what each scan means.
          </li>
        </ul>
        <button className="btn btn-primary" onClick={openAddDevice} style={{ marginBottom: 12 }}>
          <Plus size={20} />
          Register Device
        </button>
        <div className="table-container">
          {devices.length === 0 ? (
            <div className="empty-state"><p>No biometric devices registered yet.</p></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Name</th><th>Serial</th><th>Purpose</th><th>Location</th><th>Status</th><th>Last Seen</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device.id}>
                    <td>{device.name}</td>
                    <td><code>{device.device_serial}</code></td>
                    <td>{DEVICE_PURPOSE_LABELS[device.purpose] || device.purpose}</td>
                    <td>{device.location || '—'}</td>
                    <td>{device.is_active ? 'Active' : 'Deactivated'}</td>
                    <td>{device.last_seen_at ? new Date(device.last_seen_at).toLocaleString() : 'Never'}</td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Edit" onClick={() => openEditDevice(device)}>
                        <PencilSimple size={18} />
                      </button>
                      <button className="btn-icon" title={device.is_active ? 'Deactivate' : 'Activate'} onClick={() => toggleDeviceActive(device)}>
                        <X size={18} />
                      </button>
                      <button className="btn-icon btn-danger" title="Remove" onClick={() => setDeleteDeviceTarget(device.id)}>
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showKeyModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h2>New API Key</h2>
              <button type="button" className="btn-close" onClick={() => setShowKeyModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleCreateKey}>
              <div className="form-group">
                <label>Key Name *</label>
                <input type="text" value={keyForm.name} onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Scopes</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  {catalog.map((perm) => (
                    <label key={perm.code} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontWeight: 'normal' }}>
                      <input type="checkbox" checked={keyForm.scopes.includes(perm.code)} onChange={() => toggleScope(perm.code)} style={{ width: 'auto', marginTop: 3 }} />
                      <span title={perm.code}>{perm.description}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submittingKey}>{submittingKey ? 'Creating...' : 'Create'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowKeyModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showWebhookModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>New Webhook</h2>
              <button type="button" className="btn-close" onClick={() => setShowWebhookModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleCreateWebhook}>
              <div className="form-group">
                <label>Endpoint URL *</label>
                <input type="url" placeholder="https://example.com/campusio-webhook" value={webhookForm.url} onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Events</label>
                {supportedEvents.map((eventType) => (
                  <label key={eventType} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal' }}>
                    <input type="checkbox" checked={webhookForm.subscribed_events.includes(eventType)} onChange={() => toggleEvent(eventType)} style={{ width: 'auto' }} />
                    <code>{eventType}</code>
                  </label>
                ))}
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submittingWebhook}>{submittingWebhook ? 'Creating...' : 'Create'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowWebhookModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeviceModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>{editingDeviceId ? 'Edit Device' : 'Register Device'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowDeviceModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmitDevice}>
              <div className="form-group">
                <label>Device Serial *</label>
                <input
                  type="text" placeholder="ZK-A1B2C3" value={deviceForm.device_serial}
                  onChange={(e) => setDeviceForm({ ...deviceForm, device_serial: e.target.value })}
                  disabled={!!editingDeviceId} required
                />
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input type="text" placeholder="Main Gate Scanner" value={deviceForm.name} onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input type="text" placeholder="Front Gate" value={deviceForm.location} onChange={(e) => setDeviceForm({ ...deviceForm, location: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Purpose *</label>
                <select value={deviceForm.purpose} onChange={(e) => setDeviceForm({ ...deviceForm, purpose: e.target.value })} required>
                  <option value="" disabled>What is this device for?</option>
                  <option value="canteen">Canteen (identifies a student to charge their wallet)</option>
                  <option value="staff_attendance">Staff Attendance (records a clock-in/out punch)</option>
                  <option value="student_attendance">Student Attendance (records a gate check-in/out)</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submittingDevice}>{submittingDevice ? 'Saving...' : 'Save'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeviceModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteDeviceTarget}
        onClose={() => setDeleteDeviceTarget(null)}
        title="Remove Biometric Device"
        message="Punches from this device will be rejected immediately. This cannot be undone."
        type="warning"
        primaryAction={{ label: 'Remove', onClick: confirmDeleteDevice }}
        secondaryAction={{ label: 'Cancel' }}
      />

      {revealedKey && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>API Key Created</h2>
              <button type="button" className="btn-close" onClick={() => setRevealedKey(null)} aria-label="Close">×</button>
            </div>
            <p><strong>{revealedKey.name}</strong> — copy this key now. You won't be able to see it again.</p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{ flex: 1, wordBreak: 'break-all', background: 'var(--bg-secondary, #f5f5f5)', padding: 8, borderRadius: 4 }}>{revealedKey.raw_key}</code>
              <button type="button" className="btn-icon" title="Copy" onClick={() => copyToClipboard(revealedKey.raw_key)}><Copy size={18} /></button>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-primary" onClick={() => setRevealedKey(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {revealedSecret && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>Webhook Endpoint Created</h2>
              <button type="button" className="btn-close" onClick={() => setRevealedSecret(null)} aria-label="Close">×</button>
            </div>
            <p>Signing secret for <strong>{revealedSecret.url}</strong> — copy it now, you won't see it again. Verify each delivery's <code>X-Campusio-Signature</code> header with it.</p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{ flex: 1, wordBreak: 'break-all', background: 'var(--bg-secondary, #f5f5f5)', padding: 8, borderRadius: 4 }}>{revealedSecret.raw_secret}</code>
              <button type="button" className="btn-icon" title="Copy" onClick={() => copyToClipboard(revealedSecret.raw_secret)}><Copy size={18} /></button>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-primary" onClick={() => setRevealedSecret(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {deliveriesTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <h2>Deliveries — {deliveriesTarget.url}</h2>
              <button type="button" className="btn-close" onClick={() => setDeliveriesTarget(null)} aria-label="Close">×</button>
            </div>
            {deliveries.length === 0 ? (
              <p>No deliveries recorded yet.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr><th>Event</th><th>Status</th><th>Response</th><th>Attempts</th><th>Last Attempt</th></tr>
                </thead>
                <tbody>
                  {deliveries.map((d) => (
                    <tr key={d.id}>
                      <td><code>{d.event_type}</code></td>
                      <td>{d.status}</td>
                      <td>{d.response_code ?? '—'}</td>
                      <td>{d.attempts}</td>
                      <td>{d.last_attempted_at ? new Date(d.last_attempted_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title="Revoke API Key"
        message="Any integration using this key will immediately lose access. This cannot be undone."
        type="warning"
        primaryAction={{ label: 'Revoke', onClick: confirmRevoke }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!deleteWebhookTarget}
        onClose={() => setDeleteWebhookTarget(null)}
        title="Delete Webhook Endpoint"
        message="This endpoint will stop receiving events immediately. This cannot be undone."
        type="warning"
        primaryAction={{ label: 'Delete', onClick: confirmDeleteWebhook }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default IntegrationsPage;
